import React, { FC, useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Box, Button, makeStyles, createStyles, Grid }
from "@material-ui/core";
import { useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";

import { InputContainer } from "modus/components/form/input-container/InputContainer";
import { TextAreaContainer } from "modus/components/form/textarea-container/TextAreaContainer";
import ActionInliner from "legacy-components/form/action/ActionInliner";
import FormAlert from "legacy-components/form/container/FormAlert";
import GrantTypesAppCreationForm from "./GrantTypeAppCreationForm";
import { getApplicationNameValidationError } from "./ApplicationNameValidator";
import { useCurrentTeam } from "libs/security/authorization/Permission";

import ApplicationService from "pages/home/applications/components/v2.0/ApplicationService";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";

export interface AppResponse {
  applicationName: string;
  environment: string;
}

interface ResourceAppBasicFormProps {
  onCancel: () => void;
  onSuccess?: (application: AppResponse) => void;
}
const useStyles = makeStyles(() =>
  createStyles({
    formLayout: {
      display: "flex",
      width: "100%",
    },
    formContainer: {
      position: "relative",
      display: "flex",
      flexDirection: "column",
      width: "100%",
    },
    submitLoader: {
      position: "absolute",
      zIndex: 2,
      width: "100%",
      height: "100%",
      backgroundColor: "rgb(255, 255, 255, 0.32)",
    },
  })
);

const ResourceAppBasicForm: FC<ResourceAppBasicFormProps> = ({
  onCancel,
  onSuccess,
}) => {

   const { teamName: storedTeamName } = useCurrentTeam();
   const currentTeam = useSelector(
      (state: any) =>
        state?.teamManager?.teams?.currentTeam?.teamName ??
        storedTeamName
    );
  const intl = useIntl();
  const styles = useStyles();
  const [success, setSuccess] = useState(false);
  const [successProcessed, setSuccessProcessed] = useState(false);
  const [loader, setLoader] = useState(false);
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );
 const [grantTypes, setGrantTypes] = useState<string[]>([]);
 const [logoutUri, setLogoutUri] = useState<string[]>([]);
 const [redirectUri, setRedirectUri] = useState<string[]>([]);
  const history = useHistory();
  const location = useLocation();

  const canShowCreateResourceApp =
    process.env.REACT_APP_NEW_RESOURCE_APP_CREATION === "true";
  const [error, setError] = useState({
    is_error: false,
    message: "",
  });

  const onGrantTypeChange = useCallback((grantTypeList: string[]) => {
    setGrantTypes(grantTypeList);
  },[setGrantTypes]);

  const onLogoutUriChange = useCallback((logoutUri: string[]) => {
    setLogoutUri(logoutUri);
  },[setLogoutUri]);

  const onRedirectUriChange = useCallback((redirectUri: string[]) => {
    setRedirectUri(redirectUri);
  },[setRedirectUri]);

  const [formValues, setFormValues] = useState({
    name: "",
    displayName: "",
    description: "",
    type: false,
  });
  const [hasFieldError2, setHasFieldError2] = useState(false); // New state to track child component validation error
  const [hasFieldError3, setHasFieldError3] = useState(false); // New state to track child component validation error
  const applicationName = formValues.displayName.replaceAll(" ", "-");

  const getDisplayNameValidationError = useCallback(
    (value: string) => getApplicationNameValidationError(value, "Display name"),
    []
  );

  const handleSetError2 = (value: boolean) => {
    setHasFieldError2(value); // Track error from child component
  };
  const handleSetError3 = (value: boolean) => {
    setHasFieldError3(value); // Track error from child component
  };

  const FormErrorViewer = ({ errors }: { errors?: string[] }) => {
    return (
      <>
        {errors?.map((error, index) => (
          <div key={index}>{error}</div>
        ))}
      </>
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
  };

   const { state, subscribe } = useAsyncAction(() =>
       ApplicationService.createApplication(
            currentTeam?.id,
            {
              applicationType: "APPLICATION",
              name: applicationName,
              displayName: formValues.displayName,
              description: formValues.description,
              grantTypes: grantTypes,
              redirectUri: redirectUri,
              logoutUri: logoutUri,
            }
          )
    );

    useEffect(()=>{
      if(!state || successProcessed || !onCancel){
        return;
      }

      if(state.loaded && !state.error) {
        setSuccess(true);
        setLoader(false);
        setSuccessProcessed(true); // Mark as processed
        const appNameForUrl = encodeURIComponent(formValues.displayName.trim());
        if(location.pathname === "/home/applications/list" || location.pathname === "/home/apis/list") {
                                  history.push(`/home/applications/${state.value.applicationId}/gateway/${identityEnvironment}/summary?applicationName=${encodeURIComponent(appNameForUrl)}`, state.value.applicationId);
        }

        if (onSuccess) {
          const appResponse: AppResponse = {
            applicationName: formValues.displayName,
            environment: identityEnvironment
          };
          onSuccess(appResponse);
          onCancel();
        }
      } else if(state.error) {
        setError({
          is_error: true,
          message: state.error?.message || "Failed to create application"
        });
        setLoader(false);
      }
    },[state, formValues.displayName, identityEnvironment, onSuccess, successProcessed, onCancel, history, success])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoader(true);
    setSuccessProcessed(false); // Reset success processing state for new submission
    if (!hasFieldError2 && !hasFieldError3) {
      subscribe()

    } else {
      setLoader(false);
    }
  };
  return (
    <form
      className={styles.formLayout}
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <Box className={styles.formContainer}>
        {loader && <Box className={styles.submitLoader}></Box>}
        {error.is_error && (
          <FormAlert severity="error">
            <FormErrorViewer errors={[error.message]} />
          </FormAlert>
        )}
        {success && (
          <FormAlert severity="success">
            {"Application created Successfully"}
          </FormAlert>
        )}
        <Grid container>
          <Grid item md={6} sm={12}>
            <Box pr={1}>
              {/*<InputContainer
                label={intl.formatMessage({
                  defaultMessage: "Name",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter the Application Name",
                })}
                value={formValues.name}
                onChange={handleChange}
                name={"name"}
                setError={handleSetError}
                validate={{
                  required: true,
                  minLength: 3,
                  maxLength: 64,
                  pattern: "^[a-zA-Z0-9-_]+$",
                }}
                required
              ></InputContainer>*/}

              <InputContainer
                label={intl.formatMessage({
                  defaultMessage: "Application Name",
                })}
                placeholder={intl.formatMessage({
                  defaultMessage: "Enter the Application Name",
                })}
                value={formValues.displayName}
                onChange={handleChange}
                name={"displayName"}
                validate={{
                  required: true,
                  minLength: 1,
                  maxLength: 100,
                  customValidator: getDisplayNameValidationError,
                }}
                setError={handleSetError2}
                required
              ></InputContainer>

              <TextAreaContainer
                label={"Description"}
                placeholder={"Enter the details about application for reference"}
                value={formValues.description}
                name={"description"}
                onChange={handleChange}
                required
                validate={{
                  required: true,
                  minLength: 1,
                  maxLength: 255,
                  // Allows letters, numbers, punctuation, spaces, underscores, hyphens, and slashes
                  pattern: "^[\\u0000-\\u00FF\\s.!?'()_\\-/]*$",
                }}
                setError={handleSetError3}
              ></TextAreaContainer>

              
            </Box>
          </Grid>
          
              <GrantTypesAppCreationForm onGrantTypeUpdate={onGrantTypeChange} onLogoutUriChange={onLogoutUriChange} onRedirectUriChange={onRedirectUriChange}/>

        </Grid>
        
        <ActionInliner padding="inset-top">
          <Button
            onClick={onCancel}
            type="button"
            variant="outlined"
            disableElevation
          >
            {" "}
            <FormattedMessage defaultMessage="Cancel" />
          </Button>

          <Button
            onSubmit={handleSubmit}
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
            disabled={
              hasFieldError2 ||
              hasFieldError3 ||
              !formValues.displayName || 
               !formValues.description ||
              ((grantTypes.includes("authorization_code") && (logoutUri.length === 0 || redirectUri.length === 0)) || grantTypes?.length === 0)
            }
          >
            <FormattedMessage defaultMessage="Create" />
          </Button>
        </ActionInliner>
        <Box pb={2} />
      </Box>
    </form>
  );
};

export default ResourceAppBasicForm;
